import { Children, isValidElement, useEffect, useRef, useState } from "react";
import { Box, Tab, TabList, TabPanel, TabPanels, Tabs } from "@hightouch/htui";
import { TabsProps } from "@hightouch/formkit";
import { Form } from "src/formkit/components";
import { SharedProps } from ".";

type FKTabsProps = Omit<TabsProps, "tabs"> & {
  tabs: Array<Omit<TabsProps["tabs"][number], "children">>;
  children: React.ReactNode;
} & SharedProps;

export const FKTabs = ({ tabs, children }: FKTabsProps) => {
  const panels = Children.toArray(children).filter(isValidElement);

  // Controlled selection so the panels container animates between tab heights.
  const [index, setIndex] = useState(0);

  // Only the active panel is mounted (lazy Tabs)
  // The measured wrapper's height is the active panel's;
  // Mirror it onto a Box and transition height between tabs.
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>();

  useEffect(() => {
    const element = contentRef.current;
    if (!element) return;
    const observer = new ResizeObserver(() => {
      setHeight(element.offsetHeight);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <Tabs index={index} onChange={setIndex}>
      <TabList>
        {tabs.map((tab) => (
          <Tab
            key={tab.label}
            count={tab.count}
            badgeVariant={tab.badgeVariant}
            isDisabled={tab.isDisabled}
          >
            {tab.label}
          </Tab>
        ))}
      </TabList>
      <Box
        overflow="hidden"
        height={height === undefined ? undefined : `${height}px`}
        transitionProperty="height"
        transitionDuration="normal"
        transitionTimingFunction="easeOutQuad"
      >
        <Box ref={contentRef}>
          <TabPanels>
            {tabs.map((tab, tabIndex) => (
              <TabPanel key={tab.label}>
                <Form>{panels[tabIndex]}</Form>
              </TabPanel>
            ))}
          </TabPanels>
        </Box>
      </Box>
    </Tabs>
  );
};
